import {
  Backdrop,
  Badge,
  Box,
  CircularProgress,
  Container,
  Grid2,
  Paper,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import CustomTextField from "../../../../../../views/components/common/textFields/TextField";
import { SortType } from "../../../../../../models/SortType";
import FilterSidebar from "../../../../views/components/filter/FilterSidebar";
import {
  FilterMenuType,
  FilterSidebarMenu,
} from "../../../../../../models/FilterMenu";
import { ModuleType } from "../../../../../../models/ModuleType";
import { FilterValue } from "../../../../../../models/Filters/FilterValue";
import {
  ProjectManagementFilters,
  ProjectStatusFilter,
} from "../../../../../../models/ProjectManagement/ProjectManagementFilters";
import { useLocation, useNavigate } from "react-router-dom";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import ProjectManagementTable from "../../../components/projects/project-management/ProjectManagementTable";
import { useStores } from "../../../../use-stores";
import { observer } from "mobx-react";
import dayjs from "dayjs";
import { useNotification } from "../../../../../../utils/NotificationsContext";
import PermissionBlockLabel from "../../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import { EmployeeRoles } from "../../../../../../models/Employees/EmployeeRoles";
import { OrderType } from "../../../../../../models/Project/OrderType";

const ProjectManagementListing = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const { projectManagementStore, userStore, warehouseStore } = useStores();
  const navigate = useNavigate();
  const { showError } = useNotification();
  const [loading, setLoading] = useState(true);
  // const sessionFilterName = "projectManagement";
  const sessionFilterKey = "sessionFilter";
  const theme = useTheme();
  const [isParamsReady, setIsParamsReady] = useState(false);
  const parseIdValuePairs = (param: string): FilterValue[] =>
    param.split(",").map((pair) => {
      const [id, value] = pair.split(":");
      return { id: id || "", value: value || "" };
    });
  const [filterCount, setFilterCount] = useState(0);
  const filterFields = [
    "customer_id",
    "warehouse_id",
    "order_type",
    "pm_adminids",
    "sales_adminids",
    "project_type",
    "start_date",
    "end_date",
  ];

  const ALL_WAREHOUSES_OPTION: FilterValue = {
    id: "0",
    value: "All Warehouses",
  };

  const initialFilters: ProjectManagementFilters = {
    page_no: searchParams.get("page_no")
      ? parseInt((searchParams.get("page_no") as string) || "1")
      : 1,
    search_text: searchParams.get("search_text") || undefined,
    customer_id: [],
    warehouse_id: searchParams.get("warehouse_id")
      ? {
          id: searchParams.get("warehouse_id")!,
          value: searchParams.get("warehouse_id")!,
        }
      : ALL_WAREHOUSES_OPTION,
    order_id: [],
    order_type: ["1", "2", "3", "4", "5", "6"], // Default to all order types
    start_date: undefined,
    end_date: undefined,
    pm_adminids: [],
    sales_adminids: [],
    limit: parseInt(searchParams.get("limit") || "30"),
    sort_by: searchParams.get("sort_by") || "created_date",
    sort_type:
      searchParams.get("sort_type") === "ASC" ? SortType.ASC : SortType.DESC,
    project_status: searchParams.get("project_status")
      ? parseIdValuePairs(searchParams.get("project_status")!)
      : [ProjectStatusFilter[0]],
  };

  const initialParams: ProjectManagementFilters = {
    page_no: parseInt(searchParams.get("page_no") || "1"),
    search_text: searchParams.get("search_text") || undefined,
    customer_id: searchParams.get("customer_id")
      ? parseIdValuePairs(searchParams.get("customer_id")!)
      : [],
    warehouse_id: searchParams.get("warehouse_id")
      ? {
          id: searchParams.get("warehouse_id")!,
          value: searchParams.get("warehouse_id")!,
        }
      : ALL_WAREHOUSES_OPTION,
    order_id: searchParams.get("order_id")
      ? parseIdValuePairs(searchParams.get("order_id")!).map((order) =>
          parseInt(order.id),
        )
      : [],
    order_type: searchParams.get("order_type")
      ? searchParams.get("order_type")!.split(",")
      : ["1", "2", "3", "4", "5", "6"], // Default to all order types
    start_date: searchParams.get("start_date")
      ? dayjs(searchParams.get("start_date")!)
      : undefined,
    end_date: searchParams.get("end_date")
      ? dayjs(searchParams.get("end_date")!)
      : undefined,
    pm_adminids: searchParams.get("pm_adminids")
      ? parseIdValuePairs(searchParams.get("pm_adminids")!)
      : [],
    sales_adminids: searchParams.get("sales_adminids")
      ? parseIdValuePairs(searchParams.get("sales_adminids")!)
      : [],
    limit: parseInt(searchParams.get("limit") || "30"),
    sort_by: searchParams.get("sort_by") || "created_date",
    sort_type:
      searchParams.get("sort_type") === "ASC" ? SortType.ASC : SortType.DESC,
    project_status: searchParams.get("project_status")
      ? parseIdValuePairs(searchParams.get("project_status")!)
      : [ProjectStatusFilter[0]],
  };

  const [filters, setFilters] =
    useState<ProjectManagementFilters>(initialParams);
  const [params, setParams] = useState<ProjectManagementFilters>(initialParams);

  // useEffect(() => {
  //   const sessionFilterData = sessionStorage.getItem(sessionFilterKey);
  //   const existingFilters = sessionFilterData
  //     ? JSON.parse(sessionFilterData)
  //     : {};
  //   if (Array.isArray(existingFilters) && existingFilters.length > 0) {
  //     const projectManagementFilters = existingFilters.filter((filter) => {
  //       return Object.keys(filter)[0] === sessionFilterName;
  //     })[0];
  //     if (projectManagementFilters) {
  //       setParams(projectManagementFilters.projectManagement);
  //       setFilters(projectManagementFilters.projectManagement);
  //     }
  //   }
  //   isParamsReady.current = true;
  // }, []);

  useEffect(() => {
    // const sessionFilterData = sessionStorage.getItem(sessionFilterKey);
    // if (!sessionFilterData) {
    setParams(initialParams);
    setFilters(initialFilters);
    setIsParamsReady(true);
    //   return;
    // }
    // const existingFilters = JSON.parse(sessionFilterData);
    // const projectManagementFilters = existingFilters.filter(
    //   (item: any) => item.projectManagement
    // );
    // if (projectManagementFilters && projectManagementFilters.length > 0) {
    //   const existingFilter = projectManagementFilters[0].projectManagement;
    //   setParams(existingFilter);
    //   setFilters(existingFilter);
    // }
    // setIsParamsReady(true);
  }, []);

  const fetchWarehouses = async () => {
    try {
      if (warehouseStore.warehouses.length > 0) {
        return;
      }

      await warehouseStore.searchWarehouse();
    } catch (error) {
      console.error(error);
      showError("Error", "Failed to fetch warehouses.");
    }
  };

  useEffect(() => {
    if (userStore.hasPermission(EmployeeRoles.VIEW_ORDER)) {
      if (!isParamsReady) return;

      fetchWarehouses();
      projectManagementStore
        .getProjectManagementList(params)
        .then()
        .catch((err) => {
          showError(
            "Error",
            err?.response?.data?.message || "Failed to fetch project list.",
          );
        })
        .finally(() => {
          setLoading(false);
          handleFilterCount();
        });
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, isParamsReady]);

  const handleSearch = (query: string) => {
    setSearchTerm(query);
    if (query.length === 0) {
      setSearchTerm("");
      setParams((prev) => ({
        ...prev,
        search_text: "",
        page_no: 1,
      }));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      if (searchTerm.trim().length > 0) {
        setParams((prev) => ({
          ...prev,
          search_text: searchTerm,
          page_no: 1,
          sortBy: filters.sort_by || "created_date",
          sort_type: filters.sort_type || SortType.ASC,
        }));
      } else {
        setSearchTerm("");
        setParams((prev) => ({
          ...prev,
          search_text: "",
          page_no: 1,
        }));
      }
    }
  };

  const handleFilterToggle = () => {
    setOpenFilter(!openFilter);
  };

  const handleFilterChange = useCallback(
    (
      key: keyof ProjectManagementFilters,
      value: ProjectManagementFilters[keyof ProjectManagementFilters],
    ) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    [],
  );

  const filterMenu: FilterSidebarMenu[] = useMemo(() => {
    return [
      {
        moduleType: ModuleType.ORDER,
        fieldType: FilterMenuType.LOCAL_MULTI_SELECT,
        subType: "order",
        label: "Order Types",
        list: OrderType,
        value: filters?.order_type?.map((item) => item) || [],
        onChange: (selectedValues: string[]) => {
          handleFilterChange("order_type", selectedValues);
        },
      },
      {
        moduleType: ModuleType.PROJECT_MANAGEMENT,
        fieldType: FilterMenuType.LOCAL_SINGLE_SELECT,
        label: "Warehouse",
        placeholder: "Select Warehouse",
        list: [
          ALL_WAREHOUSES_OPTION,
          ...warehouseStore.warehouses.map((warehouse) => ({
            id: warehouse.id,
            value: warehouse.identifier,
          })),
        ],
        value: filters.warehouse_id
          ? [filters.warehouse_id]
          : [ALL_WAREHOUSES_OPTION],
        onChange: (value: FilterValue[]) =>
          handleFilterChange("warehouse_id", value[0]),
      },
      {
        moduleType: ModuleType.CUSTOMER,
        fieldType: FilterMenuType.MULTI_SELECT,
        subType: "search_customer",
        label: "Customer",
        placeholder: "Enter Customer name",
        value: filters.customer_id,
        onChange: (value: FilterValue[]) =>
          handleFilterChange("customer_id", value),
      },
      {
        moduleType: ModuleType.ADMIN,
        fieldType: FilterMenuType.SINGLE_SELECT,
        label: "Project Manager",
        placeholder: "Search project manager",
        value: filters.pm_adminids,
        onChange: (value: FilterValue[]) =>
          handleFilterChange("pm_adminids", value),
      },
      {
        moduleType: ModuleType.ADMIN,
        fieldType: FilterMenuType.MULTI_SELECT,
        label: "Sales POC",
        placeholder: "Search sales poc",
        value: filters.sales_adminids,
        onChange: (value: FilterValue[]) =>
          handleFilterChange("sales_adminids", value),
      },
      {
        moduleType: ModuleType.PROJECT_MANAGEMENT,
        fieldType: FilterMenuType.LOCAL_SINGLE_SELECT,
        label: "Project Status",
        list: ProjectStatusFilter,
        value: filters.project_status,
        onChange: (value: FilterValue[]) =>
          handleFilterChange("project_status", value),
      },
      {
        moduleType: ModuleType.PROJECT_MANAGEMENT,
        fieldType: FilterMenuType.DATE,
        label: "Project start date",
        value: filters.start_date,
        onChange: (selectedValues: FilterValue[]) =>
          handleFilterChange("start_date", selectedValues),
      },
      {
        moduleType: ModuleType.PROJECT_MANAGEMENT,
        fieldType: FilterMenuType.DATE,
        label: "Project end date",
        value: filters.end_date,
        onChange: (selectedValues: FilterValue[]) =>
          handleFilterChange("end_date", selectedValues),
      },
    ];

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, warehouseStore.warehouses, handleFilterChange]);

  const handleApplyFilters = () => {
    setParams((prev) => ({
      ...prev,
      ...filters,
    }));

    sessionStorage.setItem(
      sessionFilterKey,
      JSON.stringify([{ projectManagement: filters }]),
    );

    handleFilterToggle();
    handleFilterCount();
  };

  const handleClearFilters = () => {
    // Reset to default filter state (do not reuse URL-derived initialFilters,
    // otherwise "Clear Filters" won't clear warehouse/search once URL updates).
    const cleared: ProjectManagementFilters = {
      ...initialFilters,
      search_text: undefined,
      warehouse_id: ALL_WAREHOUSES_OPTION,
      order_type: ["1", "2", "3", "4", "5", "6"], // Reset to all order types
      customer_id: [],
      order_id: [],
      start_date: undefined,
      end_date: undefined,
      pm_adminids: [],
      sales_adminids: [],
      project_status: [ProjectStatusFilter[0]],
      page_no: 1,
    };

    setFilters(cleared);
    setParams(cleared);
    handleFilterToggle();
    handleFilterCount();
  };

  useEffect(() => {
    const updateQueryParams = () => {
      const queryParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          if (
            value.length > 0 &&
            typeof value[0] === "object" &&
            "id" in value[0] &&
            "value" in value[0]
          ) {
            queryParams.append(
              key,
              value
                .map((item: FilterValue) => `${item.id}:${item.value}`)
                .join(","),
            );
          }
        } else if (
          typeof value === "object" &&
          value.min !== undefined &&
          value.min !== null
        ) {
          if (value.min !== null)
            queryParams.append(`${key}_min`, String(value.min));
          if (value.max !== null)
            queryParams.append(`${key}_max`, String(value.max));
        } else if (key === "start_date" && value?.start_date) {
          const date = dayjs(value.start_date);
          if (date.isValid()) {
            queryParams.append("start_date", date.toISOString());
          }
        } else if (key === "end_date" && value?.end_date) {
          const date = dayjs(value.end_date);
          if (date.isValid()) {
            queryParams.append("end_date", date.toISOString());
          }
        } else if (
          key === "warehouse_id" &&
          typeof value === "object" &&
          value !== null &&
          "id" in (value as any) &&
          (value as any).id
        ) {
          // Persist only warehouse id in the URL so it can be rehydrated correctly.
          queryParams.append("warehouse_id", String((value as any).id));
        } else if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });

      const queryString = queryParams.toString();
      if (queryString) {
        navigate({ search: `?${queryString}` }, { replace: true });
      }
    };
    updateQueryParams();
  }, [params, navigate]);

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setParams((prev) => ({
      ...prev,
      page_no: newPage,
    }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setParams((prev) => ({
      ...prev,
      limit: newLimit,
      page_no: 1,
    }));
    setFilters((prev) => ({
      ...prev,
      limit: newLimit,
    }));
  };

  const handleSortChange = (newSortBy: string) => {
    setParams((prevParams) => ({
      ...prevParams,
      sort_by: newSortBy,
      sort_type:
        prevParams.sort_type === SortType.ASC ? SortType.DESC : SortType.ASC,
    }));
  };

  const handleFilterCount = () => {
    const count = filterFields.reduce((count, field) => {
      const value = filters[field as keyof ProjectManagementFilters];
      if (Array.isArray(value) && value.length > 0) {
        return count + 1;
      } else if (!Array.isArray(value) && value) {
        return count + 1;
      }

      return count;
    }, 0);
    setFilterCount(count);
  };

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
      {loading ? (
        <Backdrop
          sx={(theme) => ({
            color: "#fff",
            zIndex: theme.zIndex.drawer + 1,
          })}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      ) : (
        <>
          {userStore.hasPermission(EmployeeRoles.VIEW_ORDER) ? (
            <Paper
              sx={{
                padding: 5,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                }}
              >
                <CustomTypography variant="heading3" fontWeight={700}>
                  Project management
                </CustomTypography>
              </Box>

              <Grid2
                container
                spacing={5}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginY: 3,
                }}
              >
                <Grid2 size={{ xs: 12, sm: 10, md: 10 }}>
                  <CustomTextField
                    value={params.search_text ? params.search_text : searchTerm}
                    onSearch={(query) => handleSearch(query)}
                    onKeyDown={handleKeyDown}
                    width={"100%"}
                    placeholder="Search by order id or customer name"
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, sm: 2, md: 2 }}>
                  <Box>
                    <AppButton
                      variant="grayscale"
                      label="Filter"
                      startIcon={<FilterAltOutlinedIcon />}
                      onClick={() => {
                        handleFilterToggle();
                      }}
                      customStyles={{ width: "130px", height: "40px" }}
                    />

                    {filterCount ? (
                      <Badge
                        badgeContent={filterCount > 0 ? filterCount : null}
                        sx={{
                          "& .MuiBadge-badge": {
                            backgroundColor: "#1A1448",
                            color: "#fff",
                            fontSize: "11.43px",
                            fontWeight: 500,
                            borderRadius: "50%",
                            width: "24px",
                            height: "24px",
                            position: "absolute",
                            top: "-32px",
                            right: "-10px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minWidth: "20px",
                            minHeight: "20px",
                            transform: "scale(1)",
                          },
                        }}
                      />
                    ) : null}
                  </Box>
                </Grid2>
              </Grid2>

              <ProjectManagementTable
                data={projectManagementStore.projectManagementList}
                maxHeight={window.innerHeight - 350}
                limit={params.limit || projectManagementStore.limit}
                page_no={params.page_no || 1}
                total_count={projectManagementStore.total_count}
                total_pages={projectManagementStore.total_pages}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                onSortChange={handleSortChange}
                sortBy={params.sort_by!}
                sortType={params.sort_type!}
              />
            </Paper>
          ) : (
            <PermissionBlockLabel />
          )}

          {openFilter && (
            <FilterSidebar
              isOpen={openFilter}
              items={filterMenu}
              onApply={handleApplyFilters}
              handleClear={handleClearFilters}
              toggleFilter={handleFilterToggle}
              disablePastDates={false}
            />
          )}
        </>
      )}
    </Grid2>
  );
};

export default observer(ProjectManagementListing);
